import { DateTime } from "luxon"
import fp from "lodash/fp"
import ngeohash from "ngeohash"

import { valueFormatter } from "helpers/formatters/plots/plots"

export function filterObject(
  obj: { [key: string]: any },
  callback: (key: string, value: any) => boolean,
) {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => callback(key, value)),
  )
}

export const arrayRange = (start: number, stop: number, step: number) => {
  return Array.from(
    { length: (stop - start) / step + 1 },
    (_, index) => start + index * step,
  )
}

/**
 * Get 1st day of each month in an interval
 * @param {number} start datetime in millis
 * @param {number} stop datetime in millis
 *
 * @returns {number[]} - the first of month datetimes in millis in the interval
 */
export const monthsRange = (start: number, stop: number): number[] => {
  const first = DateTime.fromMillis(start)
  const last = DateTime.fromMillis(stop)
  const result: number[] = []
  for (let current = first; current <= last; current = current.plus({ month: 1 })) {
    const startOfMonth = current.set({ day: 1 })
    if (startOfMonth > first && startOfMonth <= last) {
      result.push(startOfMonth.toMillis())
    }
  }
  return result
}

/**
 * Convert a decimal degree to sign, degrees, minutes, seconds
 *
 * @param {number} degrees
 *
 * @returns {number[4]} sign, degrees, minutes, seconds
 */
const decimalToDMS = (degrees: number): number[] => {
  const sign = degrees >= 0 ? 1 : -1
  const aux = Math.abs(degrees)
  const d = aux | 0
  const m = ((aux % 1) * 60) | 0
  const s = (((aux * 60) % 1) * 60) | 0
  return [sign, d, m, s]
}

/**
 * Stringify a dms value
 *
 * @param {number[4]} dms, signed degrees, minutes, seconds
 *
 * @returns {string} d°m's"
 */
const DMStoString = (dms: number[]): string => {
  const [sign, d, m, s] = dms
  const minutes = m.toString().padStart(2, "0")
  const seconds = s.toString().padStart(2, "0")
  return `${sign >= 0 ? "" : "-"}${d}°${minutes}'${seconds}"`
}

/**
 * Format decimal degrees as a degrees, minutes, seconds string
 *
 * @param {number} degrees
 *
 * @returns {string} d°m's"
 */
export const degreesToString = (degrees: number): string => {
  const dms = decimalToDMS(degrees)
  return DMStoString(dms)
}

/**
 * Floored integer division
 *
 * together with mod they implement Knuth's modulo operation, see: https://en.wikipedia.org/wiki/Modulo
 *
 * @param {number} dividend
 * @param {number} divisor
 * @returns {number}
 */
export const quotient = (dividend: number, divisor: number): number => {
  return Math.floor(dividend / divisor)
}

/**
 * Take two numbers and returns a number between [0, max), by obtaining the remainder between the two
 *
 * together with quotient they implement Knuth's modulo operation, see: https://en.wikipedia.org/wiki/Modulo
 *
 * @param {number} num
 * @param {number} max
 * @returns {number}
 */
export const mod = (num: number, max: number): number => {
  return ((num % max) + max) % max
}

// Part of the next code was taken from https://stackoverflow.com/a/8831937/2263208
/**
 * Take a string an obtain it's hashCode (well known java hash function for strings)
 *
 * @param {string} str
 * @returns {number} an integer value
 */
export const strHash = (str: string): number => {
  let hash = 0
  if (str.length === 0) return hash
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

export const getNumberFromZeroToMaxFromStr = (str: string, max: number): number => {
  const hash = strHash(str)
  return mod(hash, max)
}

/**
 * Convert polar to cartesian coordinates
 *
 * @param {number} radius
 * @param {number} ang in degrees
 * @returns {[number, number]}
 */
export const polarToCartesian = (radius: number, ang: number): [number, number] => {
  const coordX = radius * Math.cos(Math.PI - (ang * Math.PI) / 180.0)
  const coordY = radius * Math.sin(Math.PI - (ang * Math.PI) / 180.0)

  return [coordX, coordY]
}

// Paginate a list with a default limit of 10
export const paginateList = <T>(page: number, list: T[], limit = 10): T[] => {
  const firstIndex = page * limit
  const lastIndex = (page + 1) * limit
  return list.slice(firstIndex, lastIndex)
}

/**
 * Parse a value from string to float
 * @param {string | number} value
 * @returns {number} value
 */
export const parseToFloat = (value: string | number): number => {
  return typeof value === "string" ? parseFloat(value) : value
}

/**
 * Compare, as required by sort methods
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#description
 *
 * @param a: first element
 * @param b: another element
 * @returns -1, 0 or 1, corresponding to a < b, a == b, a > b
 */
export const compare = <T>(a: T, b: T): number => {
  if (a < b) {
    return -1
  }
  if (a > b) {
    return 1
  }
  return 0
}

/**
 * Checks if value is valid, checking if it is not null, undefined or an empty string.
 * @param {any} value
 * @returns {boolean}
 */
export const isValidValue = (value: any) => {
  return !fp.isNil(value) && value !== ""
}

/**
 * Receives a number, a locale and decimalPlaces, checks if the number has decimals, and if so it formats it to the desired decimalPlaces
 * @param {number} value
 * @param {string} locale
 * @param {string | undefined} decimalPlaces
 * @returns {number}
 */
export const formatDecimalValues = (value: number, locale: string, decimalPlaces = 3) => {
  return value % 1 !== 0
    ? valueFormatter({
        value,
        decimalPlaces,
        locale,
      })
    : value
}

// Units read in thousands once they get there, as kV and km.
const PREFIXED_UNITS = new Set(["V", "m"])

/** A reading and its unit at one decimal: "400 kV", "130.8 km", "600 A". */
export const formatQuantity = (value: number, unit: string, locale: string): string =>
  valueFormatter({
    value,
    suffix: unit,
    decimalPlaces: 1,
    // Below a thousand the prefix would only ever be a milli.
    formatUnits: PREFIXED_UNITS.has(unit) && Math.abs(value) >= 1000,
    locale,
  })

export const formatFilename = (filename: string, extension: string): string => {
  return `${fp.flow(
    fp.deburr,
    fp.toLower,
    fp.replace(/[^a-z0-9-]/g, "_"),
    fp.replace(/_+/g, "_"),
    fp.trimChars("_"),
  )(filename)}.${extension}`
}

export const getGeohash = (lat: number, lon: number, precision = 6): string =>
  ngeohash.encode(lat, lon, precision)

export const decodeGeohash = (geohash: string): { lat: number; lon: number } => {
  const { latitude, longitude } = ngeohash.decode(geohash)
  return { lat: latitude, lon: longitude }
}
